import React from 'react';
import { motion } from 'framer-motion';
import StatusBadge from './StatusBadge';
import PriorityBadge from './PriorityBadge';
import { formatDate, isOverdue, getDaysUntilDue } from '../utils/dateHelpers';

const TaskCard = ({ task, onClick, users = [], weekUpdated = null }) => {
  const responsible = users.find(u => u.id === task.responsible_user_id);
  const overdue = isOverdue(task.due_date, task.status);
  const daysUntil = getDaysUntilDue(task.due_date);
  const isUrgent = daysUntil !== null && daysUntil <= 2 && daysUntil >= 0;

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={onClick}
      data-testid={`task-card-${task.id}`}
      className={`decko-card decko-card-hover p-5 cursor-pointer relative overflow-hidden ${
        overdue ? 'border-l-4 border-destructive' : 
        isUrgent ? 'border-l-4 border-warning' : ''
      }`}
      style={{
        background: overdue 
          ? 'linear-gradient(135deg, rgba(239,68,68,0.03) 0%, #FFFFFF 100%)'
          : isUrgent
          ? 'linear-gradient(135deg, rgba(245,158,11,0.03) 0%, #FFFFFF 100%)'
          : '#FFFFFF'
      }}
    >
      {/* Badge semana actualizada */}
      {weekUpdated !== null && (
        <div className={`absolute top-3 right-3 text-xs font-bold px-2 py-0.5 rounded-full ${
          weekUpdated ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'
        }`}>
          {weekUpdated ? '✓ Sem.' : '⚠ Sem.'}
        </div>
      )}
      {/* Pulse indicator si urgente */}
      {(overdue || isUrgent) && weekUpdated === null && (
        <div className={`absolute top-3 right-3 w-2.5 h-2.5 rounded-full animate-pulse ${
          overdue ? 'bg-destructive' : 'bg-warning'
        }`}></div>
      )}
      
      {/* Glowing orb si crítica */}
      {task.priority === 'Crítica' && (
        <div className="absolute top-0 right-0 w-24 h-24 bg-destructive/5 rounded-full blur-2xl"></div>
      )}
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          {task.responsabilidad && (
            <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#94A3B8' }}>
              {task.responsabilidad}
            </p>
          )}
          <h3 className="text-base font-semibold text-foreground truncate mb-2">
            {task.title}
          </h3>
          {task.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">
              {task.description}
            </p>
          )}
        </div>
      </div>

      {/* Badges */}
      <div className="flex flex-wrap gap-2 mb-3">
        <StatusBadge status={task.status} waitingType={task.waiting_type} />
        <PriorityBadge priority={task.priority} />
        {!task.responsible_user_id && (
          <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold bg-warning/10 text-warning border-2 border-warning/30 animate-pulse">
            ⚠️ Sin Responsable
          </span>
        )}
        {task.visibility === 'Internal' && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-muted text-muted-foreground border-2 border-border">
            🔒 Interna
          </span>
        )}
        {overdue && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-destructive/10 text-destructive border-2 border-destructive/30 animate-pulse">
            ⚠️ Vencida
          </span>
        )}
        {task.is_recurrent && (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border-2 border-border">
            🔁 Recurrente
          </span>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <div className="flex items-center gap-3">
          {responsible && (
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-primary font-semibold text-xs">
                {responsible.name.charAt(0).toUpperCase()}
              </div>
              <span className="truncate max-w-[100px] text-foreground font-medium">{responsible.name}</span>
            </div>
          )}
          {task.collaborator_ids && task.collaborator_ids.length > 0 && (
            <span className="text-muted-foreground">
              +{task.collaborator_ids.length} colab.
            </span>
          )}
        </div>
        {task.due_date && (
          <span className={`font-medium ${overdue ? 'text-destructive' : 'text-foreground'}`}>
            📅 {formatDate(task.due_date)}
          </span>
        )}
      </div>

      {/* Cumplimiento % */}
      {task.cumplimiento_pct != null && task.cumplimiento_pct > 0 && (
        <div className="mt-3 pt-3 border-t border-border">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">Cumplimiento</span>
            <div className="flex-1 bg-secondary rounded-full h-1.5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${task.cumplimiento_pct}%` }}
                transition={{ duration: 0.8 }}
                className={`h-full rounded-full ${task.cumplimiento_pct >= 80 ? 'bg-success' : task.cumplimiento_pct >= 50 ? 'bg-warning' : 'bg-destructive'}`}
              />
            </div>
            <span className="text-xs font-bold text-foreground">{task.cumplimiento_pct}%</span>
          </div>
        </div>
      )}

      {/* Checklist progress con gradiente */}
      {task.checklist && task.checklist.length > 0 && (
        <div className="mt-4 pt-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-secondary rounded-full h-2.5 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ 
                  width: `${(task.checklist.filter(item => item.completed).length / task.checklist.length) * 100}%` 
                }}
                transition={{ duration: 0.8 }}
                className="bg-gradient-to-r from-success to-emerald-600 h-full"
                style={{ boxShadow: '0 0 8px rgba(16,185,129,0.4)' }}
              />
            </div>
            <span className="text-xs text-foreground font-bold">
              {task.checklist.filter(item => item.completed).length}/{task.checklist.length}
            </span>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default TaskCard;
